import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Loader2, Send } from "lucide-react";

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AppPush() {
  const [status, setStatus] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/merchant/app-push").then(({ data }) => setStatus(data)).catch((e) => toast.error(extractErr(e)));

  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/merchant/app-push", { title: title.trim(), body: body.trim() });
      toast.success(`Sent to ${data.sent} of ${data.devices} installs`);
      setTitle("");
      setBody("");
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSending(false);
    }
  };

  if (!status) {
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="app-push-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading push…
      </div>
    );
  }

  const ready = Boolean(status.firebase_ready);
  const devices = Number(status.devices) || 0;

  return (
    <div className="space-y-4" data-testid="app-push">
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-bold text-lg text-slate-900">Push Notification</div>
            <p className="text-sm text-slate-500 mt-1">Send a message to people who installed your store app and allowed notifications. Other stores are never included.</p>
          </div>
        </div>
        <div className="mt-4 text-sm text-slate-700" data-testid="app-push-devices">
          <b className="font-mono-nums">{devices}</b> {devices === 1 ? "install can" : "installs can"} receive a message.
        </div>
        {!ready && (
          <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="app-push-unconfigured">
            Delivery is not connected on the platform yet. New app builds will include push once that is turned on. You do not need a Firebase account.
          </p>
        )}
      </Card>

      <Card className="p-6 border-slate-200">
        <form className="space-y-4" onSubmit={send}>
          <div>
            <Label>Title</Label>
            <Input className="mt-1" required minLength={2} maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New arrivals are in" data-testid="app-push-title" />
          </div>
          <div>
            <Label>Message</Label>
            <textarea
              required
              minLength={2}
              maxLength={240}
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Open the app to see what just landed."
              data-testid="app-push-body"
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            />
          </div>
          <Button type="submit" disabled={sending || !ready || devices === 0} data-testid="app-push-send" className="bg-blue-600 hover:bg-blue-700">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send to app installs
          </Button>
        </form>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200" data-testid="app-push-history">
        {(status.history || []).length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No messages sent yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {status.history.map((row, i) => (
              <div key={`${row.created_at}-${i}`} className="px-6 py-4" data-testid={`app-push-history-${i}`}>
                <div className="font-semibold text-slate-900">{row.title}</div>
                <div className="text-sm text-slate-600 mt-0.5">{row.body}</div>
                <div className="text-xs text-slate-400 mt-1">{when(row.created_at)} · delivered {row.sent} of {row.devices}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
